import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import "../../styles/EditURLEntryModal.css";
import "../../styles/LogoSelector.css";
import Button from "../shared/Button";
import FormField from "../shared/FormField";
import LogoSelector from "../shared/LogoSelector";
import { Logo } from "../../hooks/useLogos";
import type { QROptions } from "../../hooks/useURLEntry";

interface EditURLEntryModalProps {
  id: string;
  name: string;
  url: string;
  expirationDate: string;
  qrOptions: QROptions;
  customColor: string;
  setEditedName: (v: string) => void;
  setEditedUrl: (v: string) => void;
  setEditedExpirationDate: (v: string) => void;
  setQrOptions: (patch: Partial<QROptions>) => void;
  setCustomColor: (v: string) => void;
  onSave: (updated: {
    name: string;
    url: string;
    expirationDate: string;
    customColor: string;
    withLogo: boolean;
    selectedLogoId?: string;
    prefix: string;
  }) => void;
  onCancel: () => void;
  initialName: string;
  initialUrl: string;
  initialExpirationDate: string;
  initialQrOptions: QROptions;
  initialCustomColor: string;
  prefix: string;
  initialPrefix: string;
  availablePrefixes: { label: string; value: string }[];
  setEditedPrefix: (v: string) => void;
  allLogos: Logo[];
  onLogoDelete: (logoId: string) => Promise<boolean>;
  onLogoUpload: (file: File, name: string) => Promise<boolean>;
  validateLogoFile: (file: File) => string | null;
  validateLogoName: (name: string, excludeId?: string) => string | null;
  onDelete: (id: string) => Promise<boolean | void> | boolean | void;
}

const EditURLEntryModal: React.FC<EditURLEntryModalProps> = ({
  id,
  name,
  url,
  expirationDate,
  qrOptions,
  customColor,
  setEditedName,
  setEditedUrl,
  setEditedExpirationDate,
  setQrOptions,
  setCustomColor,
  onSave,
  onCancel,
  initialName,
  initialUrl,
  initialExpirationDate,
  initialQrOptions,
  initialCustomColor,
  prefix,
  initialPrefix,
  availablePrefixes,
  setEditedPrefix,
  allLogos,
  onLogoDelete,
  onLogoUpload,
  validateLogoFile,
  validateLogoName,
}) => {
  const modalRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onCancel();
      }
    };

    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onCancel]);

  const selectedLogoExists = qrOptions.selectedLogoId
    ? allLogos.some((logo) => logo.id === qrOptions.selectedLogoId)
    : true;

  const initialLogoExists = initialQrOptions.selectedLogoId
    ? allLogos.some((logo) => logo.id === initialQrOptions.selectedLogoId)
    : true;

  const isChanged =
    name !== initialName ||
    url !== initialUrl ||
    expirationDate !== initialExpirationDate ||
    qrOptions.withLogo !== initialQrOptions.withLogo ||
    qrOptions.selectedLogoId !== initialQrOptions.selectedLogoId ||
    customColor !== initialCustomColor ||
    prefix !== initialPrefix ||
    !selectedLogoExists ||
    !initialLogoExists;

  const handleLogoSelect = (logoId: string) => {
    setQrOptions({ selectedLogoId: logoId, withLogo: true });
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    onCancel();
  };

  return createPortal(
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div
        className="modal-content"
        onClick={(e) => e.stopPropagation()}
        ref={modalRef}
      >
        <div className="edit-url-modal-header">
          <h3>Edit Short Link</h3>
          <button
            className="edit-url-close-btn"
            onClick={onCancel}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            onSave({
              name,
              url,
              expirationDate,
              customColor,
              withLogo: qrOptions.withLogo,
              ...(qrOptions.selectedLogoId
                ? { selectedLogoId: qrOptions.selectedLogoId }
                : {}),
              prefix,
            });
          }}
        >
          <div className="edit-url-modal-body">
            <FormField
              label="Name:"
              value={name}
              onChange={(e) => setEditedName(e.target.value)}
              required
            />
            <FormField
              label="URL:"
              value={url}
              onChange={(e) => setEditedUrl(e.target.value)}
              required
            />

            <div className="logo-form-field">
              <LogoSelector
                selectedLogoId={qrOptions.selectedLogoId || ""}
                onLogoSelect={handleLogoSelect}
                allLogos={allLogos}
                onLogoDelete={onLogoDelete}
                onLogoUpload={onLogoUpload}
                validateLogoFile={validateLogoFile}
                validateLogoName={validateLogoName}
              />
              <label className="logo-field-label">Logo</label>
            </div>

            <div className="form-row">
              <FormField
                label="Expiration Date:"
                type="date"
                value={expirationDate}
                onChange={(e) => setEditedExpirationDate(e.target.value)}
                min={new Date().toISOString().split("T")[0]}
              />
              <FormField
                label="Prefix"
                type="select"
                value={prefix}
                onChange={(e) => setEditedPrefix(e.target.value)}
                options={availablePrefixes}
                required
              />
            </div>

            <div className="modal-qr-options-row">
              <div className="modal-qr-option">
                <span className="modal-switch-text">Show Logo in QR</span>
                <label className="modal-switch-label">
                  <input
                    type="checkbox"
                    checked={qrOptions.withLogo}
                    onChange={() =>
                      setQrOptions({ withLogo: !qrOptions.withLogo })
                    }
                    className="modal-switch-checkbox"
                  />
                  <span className="modal-switch-slider" />
                </label>
              </div>
              <div className="modal-qr-option">
                <span className="modal-qr-label">QR Color:</span>
                <input
                  type="color"
                  value={customColor}
                  onChange={(e) => setCustomColor(e.target.value)}
                  className="modal-color-input"
                />
              </div>
            </div>
          </div>

          <div className="edit-url-modal-footer">
            <Button
              type="button"
              className="custom-btn secondary url-card-btn"
              onClick={onCancel}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="custom-btn primary url-card-btn"
              disabled={!isChanged}
            >
              Save
            </Button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default EditURLEntryModal;
